import { GeoJSON } from '../types';

/**
 * Utility-Funktionen für GeoJSON-Geometrien
 */

/**
 * Konvertiert GeoJSON-Geometrie zu Leaflet-Koordinaten-Format
 * Leaflet erwartet: [lat, lon][] (Array von [Breitengrad, Längengrad]-Paaren)
 * GeoJSON verwendet: [lon, lat] (Längengrad zuerst)
 * 
 * @param geojson GeoJSON-Geometrie (Polygon oder MultiPolygon)
 * @returns Array von [lat, lon]-Paaren für Leaflet
 */
export const geojsonToLeafletCoordinates = (geojson: GeoJSON | undefined): [number, number][] => {
    if (!geojson || !geojson.coordinates) {
        return [];
    }

    if (geojson.type === 'Polygon') {
        // Polygon: coordinates ist [[[lon, lat], ...]]
        // Nimm den äußeren Ring (coordinates[0])
        const exteriorRing = geojson.coordinates[0] as number[][];
        if (!exteriorRing || exteriorRing.length < 3) {
            return [];
        }

        // Konvertiere [lon, lat] zu [lat, lon] für Leaflet
        return exteriorRing.map((coord: number[]) => [coord[1], coord[0]] as [number, number]);
    }

    if (geojson.type === 'MultiPolygon') {
        // MultiPolygon: coordinates ist [[[[lon, lat], ...], ...]]
        // Nimm den äußeren Ring des ersten Polygons
        const firstPolygon = geojson.coordinates[0] as number[][][];
        if (!firstPolygon || firstPolygon.length === 0) {
            return [];
        }

        const exteriorRing = firstPolygon[0] as number[][];
        if (!exteriorRing || exteriorRing.length < 3) {
            return [];
        }

        // Konvertiere [lon, lat] zu [lat, lon] für Leaflet
        return exteriorRing.map((coord: number[]) => [coord[1], coord[0]] as [number, number]);
    }

    return [];
};

/** Geografischer Mittelpunkt Deutschlands — Kartenmitte, wenn nichts anderes bekannt ist */
export const GERMANY_CENTER: [number, number] = [51.1657, 10.4515];

/**
 * Berechnet den Mittelpunkt (Centroid) einer GeoJSON-Geometrie
 *
 * @param geojson GeoJSON-Geometrie (Polygon oder MultiPolygon)
 * @returns [lat, lon] Koordinaten des Mittelpunkts, oder undefined wenn die
 *          Geometrie keine auswertbaren Koordinaten enthält
 */
export const calculateCenterFromGeoJSON = (geojson: GeoJSON | undefined): [number, number] | undefined => {
    if (!geojson || !geojson.coordinates) {
        return undefined;
    }

    // Sammle alle Koordinaten
    const allCoords: [number, number][] = [];

    if (geojson.type === 'Polygon') {
        // Polygon: Nimm alle Punkte des äußeren Rings
        const exteriorRing = geojson.coordinates[0] as number[][];
        if (exteriorRing && exteriorRing.length >= 3) {
            exteriorRing.forEach((coord: number[]) => {
                allCoords.push([coord[1], coord[0]]); // [lat, lon]
            });
        }
    } else if (geojson.type === 'MultiPolygon') {
        // MultiPolygon: Sammle alle Punkte aller Polygone
        const polygons = geojson.coordinates as number[][][][];
        polygons.forEach((polygon: number[][][]) => {
            if (polygon && polygon.length > 0) {
                const exteriorRing = polygon[0] as number[][];
                if (exteriorRing && exteriorRing.length >= 3) {
                    exteriorRing.forEach((coord: number[]) => {
                        allCoords.push([coord[1], coord[0]]); // [lat, lon]
                    });
                }
            }
        });
    }

    if (allCoords.length === 0) {
        return undefined;
    }

    // Berechne Durchschnitt
    const latSum = allCoords.reduce((sum, coord) => sum + coord[0], 0);
    const lonSum = allCoords.reduce((sum, coord) => sum + coord[1], 0);

    return [latSum / allCoords.length, lonSum / allCoords.length];
};

/**
 * Prüft, ob eine GeoJSON-Geometrie gültig ist
 * 
 * @param geojson GeoJSON-Geometrie
 * @returns true wenn gültig
 */
export const isValidGeoJSON = (geojson: GeoJSON | undefined): boolean => {
    if (!geojson || !geojson.type || !geojson.coordinates) {
        return false;
    }

    if (geojson.type === 'Polygon') {
        const exteriorRing = geojson.coordinates[0];
        return Array.isArray(exteriorRing) && exteriorRing.length >= 3;
    }

    if (geojson.type === 'MultiPolygon') {
        return Array.isArray(geojson.coordinates) && geojson.coordinates.length > 0;
    }

    return false;
};

